/**
 * Origination Processing Utilities
 *
 * Pure functions for determining asset origination with complex fallback logic.
 * Implements 6 fallback strategies in priority order:
 * 1. Self-seed check (asset is its own seed)
 * 2. Ultimate origins traversal (follow DISCOVERED chain to ultimate source)
 * 3. Parent assets fallback (immediate parent relationships)
 * 4. Asset.origins array (stored origination data)
 * 5. Asset.source field (source type fallback)
 * 6. Empty array (no origination found)
 */

import { combineAssetNodeResults } from '@/hooks/useAssets';
import { Asset, RelationshipData, TreeNode } from '@/types';
import { formatSourceForDisplay } from '@/utils/source.util';

/**
 * Dedupes origination results by (key, capability) — the variable-length
 * DISCOVERED traversal can return the same ancestor at multiple hop depths,
 * which would otherwise render as duplicate cards in the UI.
 */
function dedupeOriginations(results: OriginationResult[]): OriginationResult[] {
  const seen = new Map<string, OriginationResult>();
  for (const r of results) {
    const id = `${r.key ?? ''}|${r.capability ?? ''}`;
    if (!seen.has(id)) seen.set(id, r);
  }
  return Array.from(seen.values());
}

/**
 * Origination result type with value and display name
 */
export type OriginationResult = RelationshipData & {
  value: string;
  displayName: string;
};

/**
 * Creates a seed origination result from an asset
 *
 * @param asset - Asset with seed source
 * @returns Seed origination result
 */
function createSeedOrigination(asset: Asset): OriginationResult {
  const seedValue = asset.group || asset.identifier || asset.key;

  return {
    value: seedValue,
    capability: 'Seed domain',
    visited: asset.visited,
    created: asset.created,
    key: asset.key,
    displayName: formatSourceForDisplay('seed'),
  };
}

/**
 * Creates an origination result from relationship capability data
 *
 * @param originAsset - The origin asset
 * @param capability - The relationship capability
 * @param discoveredData - Optional relationship discovery metadata
 * @returns Capability-based origination result
 */
function createCapabilityOrigination(
  originAsset: Asset,
  capability: string,
  discoveredData?: { visited?: string; created?: string }
): OriginationResult {
  return {
    value: originAsset.identifier || originAsset.group || originAsset.key,
    capability,
    visited: discoveredData?.visited || originAsset.visited,
    created: discoveredData?.created || originAsset.created,
    key: originAsset.key,
    displayName: formatSourceForDisplay(capability),
  };
}

/**
 * Creates an origination result from integration source
 *
 * @param originAsset - The origin asset with integration source
 * @returns Integration-based origination result
 */
function createIntegrationOrigination(
  originAsset: Asset
): OriginationResult | null {
  // Skip discovery and seed sources
  if (
    !originAsset.source ||
    originAsset.source === 'discovery' ||
    originAsset.source === 'seed'
  ) {
    return null;
  }

  const integrationName =
    originAsset.source === 'account'
      ? originAsset.identifier
      : originAsset.group;

  return {
    value: integrationName,
    capability: integrationName,
    visited: originAsset.visited,
    created: originAsset.created,
    key: originAsset.key,
    displayName: formatSourceForDisplay(originAsset.source),
  };
}

/**
 * Extracts origination result from a single tree node
 *
 * @param treeNode - Graph tree node containing asset and relationship data
 * @param currentAssetKey - Current asset key to avoid self-references
 * @returns Origination result or null if invalid
 */
function extractOriginationFromTreeNode(
  treeNode: TreeNode,
  currentAssetKey: string
): OriginationResult | null {
  const originAsset = combineAssetNodeResults(treeNode)[0];
  const discoveredData =
    treeNode.node?.relationships[0]?.result?.discovereds?.[0];

  // Skip invalid or self-referencing assets
  if (!originAsset || !originAsset.key || originAsset.key === currentAssetKey) {
    return null;
  }

  // Strategy 1: Seed asset
  if (originAsset.source === 'seed') {
    return createSeedOrigination(originAsset);
  }

  // Strategy 2: Relationship capability
  if (discoveredData?.capability) {
    return createCapabilityOrigination(
      originAsset,
      discoveredData.capability,
      discoveredData
    );
  }

  // Strategy 3: Integration source
  return createIntegrationOrigination(originAsset);
}

/**
 * Processes tree nodes to extract origination results
 *
 * @param treeNodes - Array of graph tree nodes
 * @param currentAssetKey - Current asset key to avoid self-references
 * @returns Array of origination results
 */
function processTreeNodesForOriginations(
  treeNodes: TreeNode[] | undefined,
  currentAssetKey: string
): OriginationResult[] {
  if (!treeNodes || !Array.isArray(treeNodes) || treeNodes.length === 0) {
    return [];
  }

  return treeNodes
    .map(node => extractOriginationFromTreeNode(node, currentAssetKey))
    .filter((result): result is OriginationResult => result !== null);
}

/**
 * Processes parent assets with special handling for account source
 *
 * @param treeNode - Parent asset tree node
 * @param currentAssetKey - Current asset key to avoid self-references
 * @returns Origination result or null
 */
function extractOriginationFromParentNode(
  treeNode: TreeNode,
  currentAssetKey: string
): OriginationResult | null {
  const parentAsset = combineAssetNodeResults(treeNode)[0];
  const discoveredData =
    treeNode.node?.relationships[0]?.result?.discovereds?.[0];

  // Skip invalid or self-referencing assets
  if (!parentAsset || !parentAsset.key || parentAsset.key === currentAssetKey) {
    return null;
  }

  // Strategy 1: Seed asset
  if (parentAsset.source === 'seed') {
    return createSeedOrigination(parentAsset);
  }

  // Strategy 2: Relationship capability
  if (discoveredData?.capability) {
    return createCapabilityOrigination(
      parentAsset,
      discoveredData.capability,
      discoveredData
    );
  }

  // Strategy 3: Integration source with special account handling
  if (
    parentAsset.source &&
    parentAsset.source !== 'discovery' &&
    parentAsset.source !== 'seed'
  ) {
    let integrationName = parentAsset.source;
    if (parentAsset.source === 'account') {
      integrationName = 'Integration import';
    }

    return {
      value: integrationName,
      capability: integrationName,
      visited: parentAsset.visited,
      created: parentAsset.created,
      key: parentAsset.key,
      displayName: formatSourceForDisplay(parentAsset.source),
    };
  }

  return null;
}

/**
 * Processes parent assets to extract origination results
 *
 * @param parentAssets - Array of parent asset tree nodes
 * @param currentAssetKey - Current asset key to avoid self-references
 * @returns Array of origination results
 */
function processParentAssetsForOriginations(
  parentAssets: TreeNode[] | undefined,
  currentAssetKey: string
): OriginationResult[] {
  if (
    !parentAssets ||
    !Array.isArray(parentAssets) ||
    parentAssets.length === 0
  ) {
    return [];
  }

  return parentAssets
    .map(node => extractOriginationFromParentNode(node, currentAssetKey))
    .filter((result): result is OriginationResult => result !== null);
}

/**
 * Main entry point: Processes asset origination with comprehensive fallback logic
 *
 * Implements 6 fallback strategies in priority order:
 * 1. Self-seed check - If asset is a seed, it's its own origination
 * 2. Ultimate origins - Follow DISCOVERED chain to find ultimate source
 * 3. Parent assets - Use immediate parent relationships
 * 4. Asset.origins array - Use stored origination data
 * 5. Asset.source field - Use source type as fallback
 * 6. Empty array - No origination found
 *
 * @param asset - The asset to process
 * @param ultimateOrigins - Ultimate origin tree nodes from graph query
 * @param parentAssets - Parent asset tree nodes from graph query
 * @returns Array of origination results
 */
export function processAssetOriginations(
  asset: Asset,
  ultimateOrigins: TreeNode[] | undefined,
  parentAssets: TreeNode[] | undefined
): OriginationResult[] {
  // Strategy 1: Self-seed check
  if (asset.source === 'seed') {
    return [
      {
        value: 'Added as seed',
        capability: 'Seed domain',
        visited: asset.visited,
        created: asset.created,
        key: asset.key,
        displayName: formatSourceForDisplay('seed'),
      },
    ];
  }

  // Strategy 2: Ultimate origins traversal
  const ultimateOriginations = dedupeOriginations(
    processTreeNodesForOriginations(ultimateOrigins, asset.key)
  );

  if (ultimateOriginations.length > 0) {
    return ultimateOriginations;
  }

  // Strategy 3: Parent assets fallback
  const parentOriginations = dedupeOriginations(
    processParentAssetsForOriginations(parentAssets, asset.key)
  );

  if (parentOriginations.length > 0) {
    return parentOriginations;
  }

  // Strategy 4: Asset.origins array fallback
  if (asset.origins && asset.origins.length > 0) {
    return asset.origins.map(origin => ({
      value: origin,
      capability: origin,
      visited: asset.visited,
      created: asset.created,
      key: asset.key,
      displayName: formatSourceForDisplay(origin),
    }));
  }

  // Strategy 5: Asset.source fallback
  if (asset.source) {
    return [
      {
        value: asset.source,
        capability: asset.source,
        visited: asset.visited,
        created: asset.created,
        key: asset.key,
        displayName: formatSourceForDisplay(asset.source),
      },
    ];
  }

  // Strategy 6: No origination found
  return [];
}
